import Sentry from '@/sentry';

const reCAPTCHA = ({ siteKey, wire, usingReCAPTCHA = false } = {}) => {
    return {
        init(){
            const button = this.getSubmitButton();

            button.setAttribute('type', 'button');

            // if the recaptcha is off, let the form use the default behavior.
            if (!usingReCAPTCHA){
                button.setAttribute('type', 'submit');

                return;
            }

            if(
                typeof process.env.MIX_GOOGLE_RECAPTCHA_USE_LIVE_CLIENT === 'string'
                    && /^false$/i.test(process.env.MIX_GOOGLE_RECAPTCHA_USE_LIVE_CLIENT)
            ){
                this.mockRecaptcha();

                return;
            }

            this.setButtonLoading(true);

            const recaptchaSource = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;

            const scripts = Array.from(document.scripts ?? []);
            const repcatchaJS = scripts.find((script) => script?.src === recaptchaSource);

            if (repcatchaJS instanceof HTMLScriptElement){
                this.onRecaptchaLoaded();
                return;
            }

            const script = document.createElement('script');

            script.src = recaptchaSource;
            script.onload = () => this.onRecaptchaLoaded();

            document.head.appendChild(script);
        },

        onRecaptchaLoaded(){
            if(this.$root.parentElement instanceof HTMLFormElement === false){
                throw new Error('Form not found.');
            }

            const button = this.getSubmitButton();

            button.addEventListener('click', async () => await this.handleSubmit());

            try {
                grecaptcha.enterprise.ready(() => {
                    this.setButtonLoading(false);
                    this.handleRecaptchaBadge();
                });
            } catch (error) {
                Sentry.captureException(error);
                this.setButtonLoading(false);
            }
        },

        async handleSubmit(){
            const form = this.$root.parentElement;

            this.setButtonLoading(true);

            try {
                const token = await grecaptcha.enterprise.execute(siteKey, { action: 'signup' });
                
                if(wire){
                    await this.$wire.set('recaptchaToken', token);
                } else {
                    this.$refs.recaptchaToken.value = token;
                }
            } catch (error){
                Sentry.captureException(error);

                this.$dispatch('notify', {
                    title: 'Oops!',
                    type: 'warning',
                    text: "We're experiencing a problem, please try again or later.",
                    action: '/help-center/everything-else/contacting-therapist-aid',
                    actionText: 'Contact us for help'
                });

                setTimeout(() => location.reload(), 8000);

                if(wire){
                    this.$dispatch('close-register-modal');
                } else {
                    return;
                }
            }

            this.$dispatch('set-form-loading', true);

            if(wire){
                await this.$wire.call('store');
            } else {
                await form.submit();
            }

            this.setButtonLoading(false);
        },

        setButtonLoading(loading){
            const submitButton = this.getSubmitButton();

            if(loading){
                submitButton.classList.add('cursor-wait');
                submitButton.setAttribute('disabled', true);

                return;
            }

            submitButton.classList.remove('cursor-wait');
            submitButton.removeAttribute('disabled');
        },

        getSubmitButton(){
            const button = this.$root.parentElement.querySelector('[data-recaptcha-submit]');

            if(button instanceof HTMLButtonElement === false){
                throw new Error('Submit button not found.');                
            }

            return button;
        },

        handleRecaptchaBadge(){
            const badge = document.querySelector('.grecaptcha-badge');
            const badgeClassList = badge?.classList;

            wire && typeof this.open === 'boolean' && this.open === false && badgeClassList?.add('invisible');

            document.addEventListener('opened-register-modal', () => {
                badgeClassList?.add('z-40');
                badgeClassList?.remove('invisible');
            });

            document.addEventListener('closed-register-modal', () => {
                badgeClassList?.remove('z-40');
                badgeClassList?.add('invisible');
            });
        },

        mockRecaptcha(){
            if(this.$root.parentElement instanceof HTMLFormElement === false){
                throw new Error('Form not found.');
            }

            const dummyToken = Math.random().toString(36);

            if(wire){
                this.$wire.set('recaptchaToken', dummyToken);
            } else {
                this.$refs.recaptchaToken.value = dummyToken;
            }

            this.getSubmitButton().addEventListener('click', async () => {
                this.setButtonLoading(true);
                this.$dispatch('set-form-loading', true);

                if(wire){
                    await this.$wire.call('store');
                } else {
                    await this.$root.parentElement.submit();
                }

                this.setButtonLoading(false);
            });


            window.addEventListener('set-mocked-recaptcha-token', async ({ detail }) => {
                const { token } = detail;

                if(wire){
                    await this.$wire.set('recaptchaToken', token);
                    return;
                }

                this.$refs.recaptchaToken.value = token;
            });
        },

    };
}

export default reCAPTCHA;